// frontend/src/services/analytics.js
// Analytics service for batching and sending user events

import { API_BASE } from "../config/api";
import { httpRequest } from "../utils/httpClient";
import { authService } from "./auth";
import logger from '../utils/logger';

const ANALYTICS_BATCH_INTERVAL_MS = 5000;
const ANALYTICS_MAX_QUEUE_SIZE = 20;
const ANALYTICS_STORAGE_KEY = 'pista_analytics_queue';
const ANALYTICS_SESSION_KEY = 'pista_analytics_session_id';
const ANALYTICS_CONSENT_KEY = 'pista_cookie_consent';

/**
 * Generate a v4-like UUID without external dependencies
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

/**
 * Get or create a persistent session ID for anonymous journey tracking
 */
function getOrCreateSessionId() {
  try {
    const existing = localStorage.getItem(ANALYTICS_SESSION_KEY);
    if (existing) return existing;
    const sessionId = generateUUID();
    localStorage.setItem(ANALYTICS_SESSION_KEY, sessionId);
    return sessionId;
  } catch (e) {
    // Fallback if localStorage is unavailable
    return generateUUID();
  }
}

/**
 * Check if the user has given analytics consent
 * Dev: consent is always considered true and cannot be revoked.
 */
function hasAnalyticsConsent() {
  return true;
}

/**
 * Clear queued events from localStorage
 */
function clearQueuedEvents() {
  try {
    localStorage.removeItem(ANALYTICS_STORAGE_KEY);
  } catch (e) {
    // Silent failure
  }
}

/**
 * Load queued events from localStorage (for offline support)
 */
function loadQueuedEvents() {
  try {
    const raw = localStorage.getItem(ANALYTICS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

/**
 * Save queued events to localStorage
 */
function saveQueuedEvents(events) {
  try {
    localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(events));
  } catch (e) {
    // localStorage may be full; drop oldest events
    if (events.length > 0) {
      const trimmed = events.slice(-ANALYTICS_MAX_QUEUE_SIZE);
      try {
        localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(trimmed));
      } catch (e2) {
        // Silent failure
      }
    }
  }
}

class AnalyticsService {
  constructor() {
    this.queue = [];
    this.timer = null;
    this.sessionId = getOrCreateSessionId();
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.hasConsent = true;

    this._setupOnlineListener();
  }

  _setupOnlineListener() {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      this.isOnline = true;
      this._flush();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Attempt to flush any stored events on init
    const stored = loadQueuedEvents();
    if (stored.length > 0) {
      this.queue.push(...stored);
      clearQueuedEvents();
      this._scheduleFlush();
    }
  }

  /**
   * Track a single event. Batched and sent asynchronously.
   */
  track(actionType, metadata = {}) {
    const event = {
      action_type: actionType,
      metadata: metadata || {},
      page_url: typeof window !== 'undefined' ? window.location.href : null,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
      timestamp: new Date().toISOString(),
    };

    this.queue.push(event);

    if (this.queue.length >= ANALYTICS_MAX_QUEUE_SIZE) {
      this._flush();
    } else {
      this._scheduleFlush();
    }
  }

  /**
   * Schedule a flush after the batch interval
   */
  _scheduleFlush() {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this._flush();
    }, ANALYTICS_BATCH_INTERVAL_MS);
  }

  /**
   * Flush events to the backend
   */
  async _flush() {
    if (this.queue.length === 0) return;
    if (!this.isOnline) {
      saveQueuedEvents(this.queue);
      return;
    }

    const events = this.queue.splice(0, this.queue.length);

    try {
      const headers = {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
      };

      const response = await httpRequest(`${API_BASE}/analytics/track`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          events,
          session_id: this.sessionId,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        logger.warn('Analytics batch failed:', text);
        // Re-queue events on failure (except 4xx client errors)
        if (response.status >= 500) {
          this.queue.unshift(...events);
          saveQueuedEvents(this.queue);
        }
      }
    } catch (err) {
      logger.warn('Analytics flush error:', err);
      // Re-queue on network error
      this.queue.unshift(...events);
      saveQueuedEvents(this.queue);
    }
  }

  /**
   * Force flush any pending events (useful on logout/page unload)
   */
  async flushPending() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    await this._flush();
  }
}

// Singleton instance
const analyticsService = new AnalyticsService();

export { analyticsService, getOrCreateSessionId, hasAnalyticsConsent, ANALYTICS_CONSENT_KEY };
